/**
 * Axios HTTP 客户端封装
 * - 统一 baseURL: /api（Vite 代理到后端）
 * - 请求拦截器：自动注入 token
 * - 响应拦截器：统一拆包 ApiResponse.data，错误派发 api-error 事件（页面通知组件显示）
 */
import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

/** 后端统一响应包装 */
export interface ApiResponse<T = unknown> {
  code: number;
  message: string;
  data: T;
}

const TOKEN_KEY = 'partner_token';

/** 获取 token */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/** 设置 token */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/** 清除 token */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** Axios 实例 */
const http = axios.create({
  baseURL: '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** 是否正在跳转登录页（防止并发 2001 重复跳转） */
let isRedirecting = false;

/**
 * 触发跳登录页（3秒倒计时，显示具体原因）
 * 跳转前清除 localStorage 登录态，避免跳转到 /login 后又被 isLoginValid() 拉回首页
 * @param reason 原因描述：如 "服务器重启"
 */
function forceRedirectToLogin(reason: string): void {
  if (isRedirecting) return;
  isRedirecting = true;
  // 清除前端登录态，确保跳转到 /login 后不会又被检测为已登录
  localStorage.removeItem('partner_token');
  localStorage.removeItem('partner_user');
  localStorage.removeItem('partner_login_time');
  let countdown = 3;
  const tick = (): void => {
    window.dispatchEvent(
      new CustomEvent('api-error', {
        detail: {
          url: 'auth',
          method: 'AUTO',
          status: 401,
          message: `${reason}，将在 ${countdown} 秒后自动跳转到登录页面`,
          timestamp: Date.now(),
        },
      }),
    );
    countdown--;
    if (countdown > 0) {
      setTimeout(tick, 1000);
    } else {
      window.location.replace('/login');
    }
  };
  tick();
}

/** 请求拦截器：注入 Authorization */
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = token;
    }
    if (import.meta.env.DEV) {
      console.log(`[API] → ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`, config.params || config.data || '');
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/** 响应拦截器：拆包 ApiResponse + 错误事件派发 */
http.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const body = response.data;
    if (import.meta.env.DEV) {
      console.log(`[API] ← ${response.config.method?.toUpperCase()} ${response.config.url}`, body);
    }
    // 兼容非标准响应（如文件下载等直接返回数据）
    if (body === undefined || body === null) {
      return response as unknown as AxiosResponse;
    }
    // 如果不是标准 ApiResponse 结构（没有 code 字段），直接返回
    if (typeof body.code !== 'number') {
      return response;
    }
    // 业务错误
    if (body.code !== 0 && body.code !== 200) {
      const errUrl = response.config.url || 'unknown';
      const errMethod = response.config.method?.toUpperCase() || '?';

      // 处理 token 过期 / 未登录（code: 2001）— 3 秒后跳 login
      // 后端重启或 token 失效 → 跳到 login 重新登录
      if (body.code === 2001) {
        forceRedirectToLogin(body.message || '未登录或 token 已过期');
        return Promise.reject(new Error(body.message || '认证失效'));
      }

      if (import.meta.env.DEV) {
        console.warn(`[API] ⚠ 业务错误 code=${body.code} msg=${body.message} (${errMethod} ${errUrl})`);
      }
      window.dispatchEvent(
        new CustomEvent('api-error', {
          detail: {
            url: errUrl,
            method: errMethod,
            status: response.status,
            code: body.code,
            message: body.message || '操作失败',
            timestamp: Date.now(),
          },
        }),
      );
      return Promise.reject(new Error(body.message || '操作失败'));
    }
    // 拆包：返回 data 字段
    return body.data as unknown as AxiosResponse;
  },
  (error) => {
    const url = error?.config?.url || 'unknown';
    const method = error?.config?.method?.toUpperCase() || '?';
    const status = error?.response?.status;
    const msg = error?.response?.data?.message || error?.message || '未知错误';

    // 详细报错日志（开发环境）
    if (import.meta.env.DEV) {
      console.error(`[API] ✗ ${method} ${url} → status=${status} msg=${msg}`, error);
    }

    // 401 未授权 → 3 秒后跳 login
    if (status === 401) {
      forceRedirectToLogin(msg || '认证失败');
      return Promise.reject(new Error(msg || '认证失败'));
    }

    // 网络错误
    if (error?.message === 'Network Error') {
      window.dispatchEvent(
        new CustomEvent('api-error', {
          detail: { url, method, status: undefined, message: '网络连接失败，后端不可达', timestamp: Date.now() },
        }),
      );
      return Promise.reject(new Error('网络连接失败，后端不可达'));
    }
    // 超时
    if (error?.code === 'ECONNABORTED') {
      window.dispatchEvent(
        new CustomEvent('api-error', {
          detail: { url, method, status: undefined, message: '请求超时 15s', timestamp: Date.now() },
        }),
      );
      return Promise.reject(new Error('请求超时，请稍后重试'));
    }

    // 其他 HTTP 错误（404/403/500 等）也派发事件
    window.dispatchEvent(
      new CustomEvent('api-error', {
        detail: { url, method, status, message: msg, timestamp: Date.now() },
      }),
    );
    return Promise.reject(new Error(`${status ?? '?'} ${method} ${url}: ${msg}`));
  },
);

export default http;
